import React, { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, User as UserIcon, ListOrdered, LogOut, Settings } from "lucide-react";
const STATUS_TINTS = {
  new: "bg-brass/15 text-brass border-brass/30",
  in_progress: "bg-blue-400/15 text-blue-300 border-blue-400/30",
  scheduled: "bg-purple-400/15 text-purple-300 border-purple-400/30",
  completed: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  cancelled: "bg-red-400/15 text-red-300 border-red-400/30",
};

export default function Dashboard() {
  const { t, lang, setLang } = useI18n();
  const { user, loading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [profileMsg, setProfileMsg] = useState("");
  const [profileErr, setProfileErr] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", phone: user.phone || "" });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/leads/me");
        setOrders(data || []);
      } catch {}
      setLoadingOrders(false);
    })();
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileErr(""); setProfileMsg("");
    try {
      await api.patch("/auth/profile", profile);
      await refreshUser();
      setProfileMsg(t.dashboard.saved);
      setTimeout(() => setProfileMsg(""), 2000);
    } catch (err) {
      setProfileErr(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-onyx">
        <div className="text-bone/60 text-sm tracking-widest uppercase">Loading…</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-onyx text-bone flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 border-r border-bone/10 flex-col p-8 sticky top-0 h-screen">
        <Link to="/" data-testid="dash-back" className="flex items-center gap-2 text-bone/60 hover:text-brass text-[0.7rem] tracking-[0.2em] uppercase">
          <ArrowLeft className="w-4 h-4" /> На головну
        </Link>
        <div className="mt-12">
          <span className="font-display italic-display text-4xl text-brass">Д</span>
          <div className="font-display text-2xl mt-2">Кабінет</div>
        </div>

        <nav className="mt-12 flex flex-col gap-1">
          <button onClick={() => setTab("orders")} data-testid="tab-orders" className={`flex items-center gap-3 px-4 py-3 text-sm tracking-[0.12em] uppercase transition-colors ${tab === "orders" ? "bg-charcoal text-brass border-l-2 border-brass" : "text-bone/60 hover:text-bone"}`}>
            <ListOrdered className="w-4 h-4" /> {t.dashboard.orders}
          </button>
          <button onClick={() => setTab("profile")} data-testid="tab-profile" className={`flex items-center gap-3 px-4 py-3 text-sm tracking-[0.12em] uppercase transition-colors ${tab === "profile" ? "bg-charcoal text-brass border-l-2 border-brass" : "text-bone/60 hover:text-bone"}`}>
            <UserIcon className="w-4 h-4" /> {t.dashboard.profile}
          </button>
          <button onClick={() => setTab("settings")} data-testid="tab-settings" className={`flex items-center gap-3 px-4 py-3 text-sm tracking-[0.12em] uppercase transition-colors ${tab === "settings" ? "bg-charcoal text-brass border-l-2 border-brass" : "text-bone/60 hover:text-bone"}`}>
            <Settings className="w-4 h-4" /> {t.dashboard.settings}
          </button>
        </nav>

        <div className="mt-auto pt-8 border-t border-bone/10">
          <div className="text-xs text-taupe truncate">{user.email}</div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="dash-logout" className="mt-3 flex items-center gap-2 text-bone/70 hover:text-brass text-[0.7rem] tracking-[0.2em] uppercase">
            <LogOut className="w-3.5 h-3.5" /> {t.cta.logout}
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 lg:p-12 max-w-5xl">
        <div className="mb-12">
          <div className="eyebrow mb-3">— {t.dashboard.hi}</div>
          <h1 className="font-display text-4xl lg:text-6xl tracking-tight">
            {user.name?.split(" ")[0] || user.email}
            <span className="text-brass">.</span>
          </h1>
        </div>

        {tab === "orders" && (
          <section data-testid="orders-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl tracking-tight">{t.dashboard.orders}</h2>
              <span className="text-xs text-taupe">{orders.length}</span>
            </div>
            {loadingOrders ? (
              <div className="text-bone/50 text-sm">…</div>
            ) : orders.length === 0 ? (
              <div className="border border-bone/10 p-8 text-bone/60 text-sm">{t.dashboard.no_orders}</div>
            ) : (
              <div className="border border-bone/10">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`order-${o.id}`} className="grid grid-cols-12 gap-4 p-5 border-b border-bone/8 last:border-b-0 hover:bg-charcoal/60 transition-colors">
                    <div className="col-span-12 md:col-span-3">
                      <div className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{new Date(o.created_at).toLocaleString("uk-UA")}</div>
                      <div className="font-display text-xl mt-1">{o.service || "—"}</div>
                    </div>
                    <div className="col-span-6 md:col-span-3 text-sm">
                      <div className="text-bone/60 text-xs">Місто</div>
                      <div className="text-bone">{o.city || "—"}</div>
                    </div>
                    <div className="col-span-6 md:col-span-4 text-sm">
                      <div className="text-bone/60 text-xs">Повідомлення</div>
                      <div className="text-bone/90 line-clamp-2">{o.message || "—"}</div>
                    </div>
                    <div className="col-span-12 md:col-span-2 flex md:justify-end items-center">
                      <span className={`text-[0.65rem] tracking-[0.2em] uppercase border px-3 py-1 rounded-full ${STATUS_TINTS[o.status] || STATUS_TINTS.new}`}>
                        {t.dashboard.status[o.status] || o.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {(tab === "profile" || tab === "settings") && (
          <section data-testid="profile-tab" className="max-w-xl">
            <h2 className="font-display text-2xl tracking-tight mb-6">{t.dashboard.profile}</h2>
            <form onSubmit={saveProfile} className="space-y-2 bg-charcoal border border-bone/10 p-8">
              <div>
                <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.auth.name}</label>
                <input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} className="field" />
              </div>
              <div>
                <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.auth.phone}</label>
                <input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} className="field" />
              </div>
              <div>
                <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.auth.email}</label>
                <input value={user.email} disabled className="field opacity-60" />
              </div>
              <div className="pt-4">
                <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe block mb-2">{t.dashboard.lang_label}</label>
                <div className="flex gap-2">
                  {["ua","ru","en"].map((l) => (
                    <button
                      type="button"
                      key={l}
                      data-testid={`profile-lang-${l}`}
                      onClick={() => { setLang(l); setProfileMsg(t.dashboard.saved); setTimeout(() => setProfileMsg(""), 1500); }}
                      className={`px-3 py-2 text-[0.7rem] tracking-[0.18em] uppercase border transition-all ${lang === l ? "border-brass text-brass" : "border-bone/15 text-bone/60 hover:text-bone"}`}
                    >
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              {profileErr && <div className="text-red-400 text-sm">{profileErr}</div>}
              {profileMsg && <div data-testid="profile-saved" className="text-brass text-sm">{profileMsg}</div>}
              <button type="submit" data-testid="profile-save" className="btn-brass mt-4">{t.dashboard.save}</button>
            </form>
          </section>
        )}
      </main>
    </div>
  );
}
